import { useContext, useEffect, useRef } from "react";

import { AnalyticsSessionParamsContext } from "context/analyticsSessionParams";
import {
  logCommerceCollectionView,
  logCommerceProductView,
} from "helpers/commerceAnalytics";

export interface CommerceViewOptions {
  /** Include the route when one component can represent more than one view. */
  viewKey?: string;
  enabled?: boolean;
}

function analyticsSessionIsReady(analyticsSessionParams: unknown): boolean {
  return (
    analyticsSessionParams !== null && analyticsSessionParams !== undefined
  );
}

/**
 * Emits the drinks collection contract after the shared page-view/session
 * bootstrap is ready. A stable key prevents React rerenders from duplicating
 * the event while allowing a different route key to represent a new view.
 */
export function useCommerceCollectionView(
  options: CommerceViewOptions = {}
): void {
  const { analyticsSessionParams } = useContext(AnalyticsSessionParamsContext);
  const lastViewKey = useRef<string | null>(null);
  const { enabled = true, viewKey = "/collections/drinks" } = options;
  const eventKey = `collection:${viewKey}`;

  useEffect(() => {
    if (
      !enabled ||
      !analyticsSessionIsReady(analyticsSessionParams) ||
      lastViewKey.current === eventKey
    ) {
      return;
    }

    if (logCommerceCollectionView()) {
      lastViewKey.current = eventKey;
    }
  }, [analyticsSessionParams, enabled, eventKey]);
}

/**
 * Emits one product contract per handle/route view. Passing router.asPath as
 * viewKey also treats a client-side transition to another path as a new view.
 */
export function useCommerceProductView(
  handle: string | undefined,
  options: CommerceViewOptions = {}
): void {
  const { analyticsSessionParams } = useContext(AnalyticsSessionParamsContext);
  const lastViewKey = useRef<string | null>(null);
  const { enabled = true, viewKey = handle ?? "" } = options;
  const eventKey = handle ? `product:${handle}:${viewKey}` : "";

  useEffect(() => {
    if (
      !handle ||
      !enabled ||
      !analyticsSessionIsReady(analyticsSessionParams) ||
      lastViewKey.current === eventKey
    ) {
      return;
    }

    if (logCommerceProductView(handle)) {
      lastViewKey.current = eventKey;
    }
  }, [analyticsSessionParams, enabled, eventKey, handle]);
}
